import { Control, FieldPath, FieldValues } from 'react-hook-form'

import { TokenWithBalance } from '@/domain/common/types'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { AssetInputProps } from '@/ui/molecules/asset-input/AssetInput'
import { cn } from '@/ui/utils/style'

import { raise } from '@/utils/assert'
import { AssetSelector } from '../../molecules/asset-selector/AssetSelector'
import { ControlledMultiSelectorAssetInput } from '../multi-selector/ControlledMultiSelectorAssetInput'
import { BalanceType } from '@/features/dialogs/common/types'
import { SearchableAssetSelector } from '@/ui/molecules/searchable-asset-selector/SearchableAssetSelector'

export interface AssetSelectorWithInputProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  fieldName: FieldPath<TFieldValues>
  selectorAssets: TokenWithBalance[]
  selectedAsset: TokenWithBalance
  setSelectedAsset?: (selectedAsset: TokenSymbol) => void
  maxValue?: NormalizedUnitNumber
  balanceType?: BalanceType
  displayBalance?: NormalizedUnitNumber
  maxSelectedFieldName?: string
  removeSelectedAsset?: () => void
  disabled?: boolean
  showError?: boolean // defaults to show error if field is touched or dirty
  className?: string
  variant?: AssetInputProps['variant']
  walletIconLabel?: string
  resetBorrowStatus?: () => void
  withSearch?: boolean
  customOnChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

export function AssetSelectorWithInput<TFieldValues extends FieldValues>({
  control,
  fieldName,
  selectorAssets,
  selectedAsset,
  setSelectedAsset,
  maxValue,
  balanceType,
  displayBalance,
  removeSelectedAsset,
  disabled,
  showError,
  className,
  variant,
  walletIconLabel,
  maxSelectedFieldName,
  resetBorrowStatus,
  withSearch,
  customOnChange,
}: AssetSelectorWithInputProps<TFieldValues>) {
  if (selectorAssets.length > 1 && !setSelectedAsset) {
    raise('When more than one option to choose from, asset selection setter must be provided')
  }

  const AssetSelectorComponent = withSearch ? SearchableAssetSelector : AssetSelector
  return (
    <div className={cn('flex w-full flex-row justify-between gap-2', className)}>
      <AssetSelectorComponent
        assets={selectorAssets}
        selectedAsset={selectedAsset.token}
        setSelectedAsset={(newAsset) => setSelectedAsset?.(newAsset)}
        disabled={disabled}
        resetBorrowStatus={resetBorrowStatus}
      />
      <ControlledMultiSelectorAssetInput
        fieldName={fieldName}
        control={control}
        disabled={disabled}
        token={selectedAsset.token}
        onRemove={removeSelectedAsset}
        balance={displayBalance ?? selectedAsset.balance}
        balanceType={balanceType}
        max={maxValue}
        maxSelectedFieldName={maxSelectedFieldName}
        showError={showError}
        variant={variant}
        walletIconLabel={walletIconLabel}
        resetBorrowStatus={resetBorrowStatus}
        customOnChange={customOnChange}
      />
    </div>
  )
}
